/* eslint-disable @typescript-eslint/no-non-null-assertion */
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { gettext } from "web/script/modules/django-i18n";
import { formatPrice } from "web/script/modules/price";
import styles from "./multi-range-slider.module.css";

interface MultiRangeSliderProps {
    min?: number;
    max?: number;
    maxPlus?: number;
    onChange?(min: number, max: number);
    isActiveMin?: boolean;
    isActiveMax?: boolean;
    minActiveValue?: number;
    maxActiveValue?: number;
}

function getPercent(value: number, min: number, max: number): number {
    return Math.round(((value - min!) / (max! - min!)) * 100);
}

export function MultiRangeSlider({
    min = 0,
    max = 1000,
    onChange,
    maxPlus = 1000000,
    isActiveMin,
    isActiveMax,
    minActiveValue,
    maxActiveValue,
}: MultiRangeSliderProps): JSX.Element {
    const [minVal, setMinVal] = useState(min);
    const [maxVal, setMaxVal] = useState(max);

    const minInput = useRef<HTMLInputElement>(null);
    const maxInput = useRef<HTMLInputElement>(null);

    let minPercent: number = getPercent(minVal, min, max);
    let maxPercent: number = getPercent(maxVal, min, max);

    useEffect(() => {
        if (!isActiveMin) {
            setMinVal(min);
        } else if (minActiveValue) {
            setMinVal(minActiveValue);
        }
        if (!isActiveMax) {
            setMaxVal(max);
        } else if (maxActiveValue) {
            setMaxVal(maxActiveValue);
        }
    }, [max, min, isActiveMax, isActiveMin, minActiveValue, maxActiveValue]);

    return (
        <>
            <div className={styles.slider}>
                <input
                    aria-label={`${gettext("feed.min_price_slider.aria_label")}`}
                    type="range"
                    min={min}
                    max={max}
                    value={minVal}
                    ref={minInput}
                    onChange={(event) => {
                        const value = Math.min(Number(event.target.value), maxVal! - 1);
                        setMinVal(value);
                    }}
                    onTouchEnd={() => {
                        const myMax = maxVal === max ? maxPlus : maxVal;
                        onChange?.(minVal, myMax);
                    }}
                    className={clsx(styles.thumb, styles.thumbZIndex3, {
                        [styles.thumbZIndex5]: minVal! > max! - 100,
                    })}
                />
                <input
                    aria-label={`${gettext("feed.max_price_slider.aria_label")}`}
                    type="range"
                    min={min}
                    max={max}
                    value={maxVal}
                    ref={maxInput}
                    onChange={(event) => {
                        const value = Math.max(Number(event.target.value), minVal! - 1);
                        setMaxVal(value);
                    }}
                    onTouchEnd={() => {
                        const myMax = maxVal === max ? maxPlus : maxVal;
                        onChange?.(minVal, myMax);
                    }}
                    className={clsx(styles.thumb, styles.thumbZIndex4)}
                />
                <div className={styles.sliderTrack} />
                <div
                    className={styles.sliderRange}
                    style={{
                        left: `${minPercent}%`,
                        width: `${maxPercent - minPercent}%`,
                    }}
                />
                <div className={styles.leftValue}>{formatPrice(minVal).round().withCurrency()}</div>

                {maxVal === max ? (
                    <div className={styles.rightValue}>
                        {formatPrice(maxVal).round().withCurrency() + "+"}
                    </div>
                ) : (
                    <div className={styles.rightValue}>
                        {formatPrice(maxVal).round().withCurrency()}
                    </div>
                )}
            </div>
        </>
    );
}
